import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl-next';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMatch, useParams } from 'src/router/hooks';

import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { Stack } from '@atlaskit/primitives';
import AiOptIn from '@atlassian/ai-opt-in';

import messages from './ai-opt-in-modal.i18n';
import { AiOptInContainer } from './styles';
import urls from './urls';

export type AiOptInModalProps = {
  showAiOptInModal: boolean;
  setShowAiOptInModal: (showAiOptInModal: boolean) => void;
  isWorkspaceAdmin: boolean;
};

/**
 * Modal shown for users to opt-in to Atlassian Intelligence
 *
 * Users will see the generic modal if they are not a workspace admin
 * Admins will see a modal with a link to the AI workspace settings page
 *
 * @param showAiOptInModal - Boolean to control whether the modal renders
 * @param setShowAiOptInModal - Function to set the boolean controlling whether the modal renders
 * @param isWorkspaceAdmin - Boolean that indicates whether the user is a workspace admin
 */

export const AiOptInModal = ({
  showAiOptInModal,
  setShowAiOptInModal,
  isWorkspaceAdmin,
}: AiOptInModalProps): JSX.Element => {
  const closeModal = useCallback(
    () => setShowAiOptInModal(false),
    [setShowAiOptInModal]
  );
  const { repositoryOwner } = useParams<{ repositoryOwner: string }>();
  const { path } = useMatch();
  const optInUrl = urls.internal.optIn(repositoryOwner);

  const adminDescription = (
    <Stack space="space.100">
      <FormattedMessage {...messages.adminDescriptionOne} tagName="span" />
      <FormattedMessage
        {...messages.adminDescriptionTwo}
        tagName="span"
        values={{
          atlassianIntelligenceLink: (
            <a href={urls.external.trustLink} target="_blank">
              <FormattedMessage {...messages.adminDescriptionTwoLink} />
            </a>
          ),
        }}
      />
    </Stack>
  );

  let source = '';
  switch (path) {
    case '/:repositoryOwner/:repositorySlug/pull-requests/new':
      source = 'createPullRequestEditorToolbar';
      break;
    case '/:repositoryOwner/:repositorySlug/pull-requests/:pullRequestId':
      source = 'pullRequestEditorToolbar';
      break;
    case '/:repositoryOwner/:repositorySlug/commits/:ref+':
      source = 'commitPageEditorToolbar';
  }

  return (
    <ModalTransition>
      {showAiOptInModal && (
        <Modal onClose={closeModal} width="450">
          <AiOptInContainer>
            <AiOptIn
              source={source}
              product="bitbucket"
              isOrgAdmin={isWorkspaceAdmin}
              onAdminClose={closeModal}
              adminLink={optInUrl}
              adminBodyText={adminDescription}
            />
          </AiOptInContainer>
        </Modal>
      )}
    </ModalTransition>
  );
};
